import React from 'react';
import { compact } from 'lodash';
import { shouldShowBumpTag } from '@reverbdotcom/commons/src/components/bump_tag';
import {
  Core_Marketplace_CombinedMarketplaceSearchQuery,
  Input_reverb_search_ListingsSearchRequest,
} from '@reverbdotcom/commons/src/gql/graphql';
import { PageSettings } from './map_params';
import { IUser } from '@reverbdotcom/commons/src/components/user_context_provider';
import TileCurationView from '../discovery/curation/tile_curation_view';
import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import ListViewRowCard from '../list_view_row_card';
import FeaturedListingsRow, { MIN_MARKETPLACE_LISTINGS } from './featured_listings_row';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import { RCListingGrid } from '@reverbdotcom/cadence/components';
import { useExperimentFlags } from '../../context/ExperimentContext';

const COMPONENT_NAME = 'InterspersedListings';

type IListing = Core_Marketplace_CombinedMarketplaceSearchQuery['bumpedSearch']['listings'][0];

interface IListingsProps {
  listing: IListing;
  idx: number;
  user: Partial<IUser>;
  trackingName?: string;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  includeItemListMetadata?: boolean;
}

function listingKey(listing: IListing): string {
  if (listing.bumpKey?.key) {
    return `${listing.id}-${listing.bumpKey?.key}`;
  }

  return listing.id;
}

export function Listing({
  listing,
  idx,
  user,
  trackingName = COMPONENT_NAME,
  trackingQuery = '',
  trackingFilter = '',
  trackingSort = '',
  trackingPage = 1,
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  includeItemListMetadata = false,
}: IListingsProps) {
  const { isBumpDisplayExpEnabled, isPriceRecExpEnabled } = useExperimentFlags();
  if (displayAsRows) {
    return (
      <ListViewRowCard
        key={listing.id}
        listing={listing}
        loading={loading}
        position={idx}
        trackingQuery={trackingQuery}
        trackingFilter={trackingFilter}
        trackingSort={trackingSort}
        trackingPage={trackingPage}
        displayGreatValue={true}
        titleHtmlTag="h2"
        isBumpDisplayExpEnabled={isBumpDisplayExpEnabled}
        includeItemListMetadata={includeItemListMetadata}
        isPriceRecExpEnabled={isPriceRecExpEnabled}
        enableSwiper
      />
    );
  }

  return (
    <ListingCard
      key={listing.id}
      listing={listing}
      position={idx}
      trackingName={trackingName}
      trackingQuery={trackingQuery}
      trackingFilter={trackingFilter}
      trackingSort={trackingSort}
      trackingPage={trackingPage}
      watchBadgeParentComponentName={WatchParentComponentNames.MarketplaceGridWatch}
      titleHtmlTag="h2"
      isBumpDisplayExpEnabled={isBumpDisplayExpEnabled}
      isPriceRecExpEnabled={isPriceRecExpEnabled}
      footerContent={
        user.isAdmin &&
        <TileCurationView
          listingId={listing.id}
          pageSettings={pageSettings}
          bumped={listing.bumped}
        />
      }
      includeItemListMetadata={includeItemListMetadata}
      enableSwiper
    />
  );
}

// This function intersperses the bump listings amongst normal listings.
// It is setup so that every 2nd and 3rd tile (of out a set of 7) in the grid are bumped listings.
function intersperseBumps(listings, bumps, starting, frequency) {
  let bumpIndex = 0;
  const interspersedArray = [];
  for (let i = 0; i < listings.length; i += 1) {
    if ((i + starting) % frequency === 0) {
      interspersedArray.push(bumps[bumpIndex], bumps[bumpIndex + 1]);
      bumpIndex += 2;
    }
    interspersedArray.push(listings[i]);
  }

  return compact(interspersedArray);
}

function setTrackingName(listing) {
  return shouldShowBumpTag(listing) ? `Bumped${COMPONENT_NAME}` : COMPONENT_NAME;
}

interface ListingItemProps {
  listing: IListing;
  idx: number;
  user: Partial<IUser>;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  includeItemListMetadata?: boolean;
}

export function ListingItem({
  listing,
  idx,
  user,
  trackingQuery = '',
  trackingFilter,
  trackingSort,
  trackingPage,
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  includeItemListMetadata = false,
}: ListingItemProps) {
  const listingElement = (
    <Listing
      listing={listing}
      idx={idx}
      user={user}
      trackingName={setTrackingName(listing)}
      trackingQuery={trackingQuery}
      trackingFilter={trackingFilter}
      trackingSort={trackingSort}
      trackingPage={trackingPage}
      pageSettings={pageSettings}
      displayAsRows={displayAsRows}
      loading={loading}
      includeItemListMetadata={includeItemListMetadata}
    />
  );

  return (
    <RCListingGrid.Item colSpan={displayAsRows ? 'all' : 'one'}>
      {listingElement}
    </RCListingGrid.Item>
  );
}

interface IProps {
  user: Partial<IUser>;
  listings: IListing[];
  bumpedListings: IListing[];
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  pageSettings?: PageSettings;
  displayAsRows?: boolean;
  loading?: boolean;
  includeItemListMetadata?: boolean;
  intersperseListingsLimit?: number;
  alwaysShowBumpListings?: boolean;
  showFeaturedListingsRow?: boolean;
  searchParams?: Input_reverb_search_ListingsSearchRequest;
}

export default function InterspersedListings({
  user,
  listings,
  bumpedListings,
  trackingQuery = '',
  trackingFilter = '',
  trackingSort = '',
  trackingPage = 1,
  pageSettings = undefined,
  displayAsRows = false,
  loading = false,
  alwaysShowBumpListings = false,
  includeItemListMetadata = false,
  intersperseListingsLimit = 25,
  showFeaturedListingsRow = false,
  searchParams = {},
}: IProps) {
  const combinedListings = intersperseBumps(listings, bumpedListings, 4, 5);

  // the marketplace rollup experiment collapses organic listings, when in this exp.
  // we always want to show bumps because there will be less duplication and we are trying to drive bump sales
  const listingsToUse = (listings.length < intersperseListingsLimit && !alwaysShowBumpListings) ? listings : combinedListings;

  // Add on any bumped listings at the end if the interspered array does not already contain them
  // This helps fix issues where we might not show all the bump listings if there are very few organic listings due to collapsing.
  if (alwaysShowBumpListings) {
    bumpedListings.forEach((bumpedListing) => {
      if (!combinedListings.includes(bumpedListing)) {
        combinedListings.push(bumpedListing);
      }
    });
  }

  // Used for displaying a featured listings row in the middle row of the listings grid.
  const middleIdx = Math.floor(listingsToUse.length / 2);

  const shouldPlaceFeaturedListingsRow = (idx: number) =>
    showFeaturedListingsRow &&
    listingsToUse.length >= MIN_MARKETPLACE_LISTINGS &&
    idx === middleIdx;

  return (
    <>
      {listingsToUse.map((listing, idx) => (
        <React.Fragment key={listingKey(listing)}>
          {shouldPlaceFeaturedListingsRow(idx) && <FeaturedListingsRow searchParams={searchParams} />}
          <ListingItem
            listing={listing}
            idx={idx}
            user={user}
            trackingQuery={trackingQuery}
            trackingFilter={trackingFilter}
            trackingSort={trackingSort}
            trackingPage={trackingPage}
            pageSettings={pageSettings}
            displayAsRows={displayAsRows}
            loading={loading}
            includeItemListMetadata={includeItemListMetadata}
          />
        </React.Fragment>
      ))}
    </>
  );
}
